import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { CONTROL_FIELD_CLASS } from "@/components/ui/control-scale";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { DOCUMENT_SEARCH_IDLE_MS } from "../../constants/search";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

interface DocumentSearchFieldProps {
  /** The committed query — what the rows on screen are (or are becoming). */
  value: string;
  /** Called with the trimmed text whenever the user commits a search. */
  onCommit: (query: string) => void;
  /** True while the committed query is still resolving into rows. */
  isBusy?: boolean;
  className?: string;
  /** Overrides the default "Search documents…" placeholder. */
  placeholder?: string;
}

/**
 * The documents search box.
 *
 * **Search commits on a deliberate action, never on a keystroke**: Enter (the
 * surrounding form), the leading search button, the clear button, or
 * `DOCUMENT_SEARCH_IDLE_MS` of not typing. Typing alone changes nothing but the
 * text in the box — see `constants/search.ts` for why.
 *
 * Two affordances keep that honest, because a box that has swallowed your text
 * without searching looks identical to one that is broken:
 *  - uncommitted text turns the search button from muted to foreground and
 *    relabels it "Press Enter to search";
 *  - `isBusy` swaps that button for a spinner, from the moment a search is
 *    dispatched until the rows for it have landed.
 *
 * Presentational: the caller owns the committed value and the busy flag.
 * `DocumentSearchInput` binds those to the shared filters context; the folder
 * view binds them to its own state on /projects, where no provider exists.
 */
export function DocumentSearchField({
  value,
  onCommit,
  isBusy = false,
  className,
  placeholder,
}: DocumentSearchFieldProps) {
  const { t } = useLanguage();

  const [localValue, setLocalValue] = useState(value);

  // What this box last handed to `onCommit`. The mirror below compares against
  // it rather than against `value`, so committing a trimmed query doesn't bounce
  // the untrimmed text the user is still holding out of the input.
  const lastCommittedRef = useRef(value);

  const commit = useCallback(
    (next: string) => {
      const trimmed = next.trim();
      lastCommittedRef.current = trimmed;
      onCommit(trimmed);
    },
    [onCommit]
  );

  // The idle fallback. `useDebouncedValue` settles `DOCUMENT_SEARCH_IDLE_MS`
  // after the last edit; committing the settled value is then a no-op whenever
  // Enter or a button got there first, since every committer trims the same way
  // and the context setter bails on an unchanged query.
  const idleValue = useDebouncedValue(localValue, DOCUMENT_SEARCH_IDLE_MS);
  useEffect(() => {
    if (idleValue.trim() === lastCommittedRef.current) return;
    commit(idleValue);
  }, [idleValue, commit]);

  // Mirror an externally-driven query change (a filter reset, a view that seeds
  // the query) into the box. Adjusting state during render rather than in an
  // effect keeps it to a single render pass — an effect here would commit the
  // stale value first and cascade a second render.
  const [lastExternalValue, setLastExternalValue] = useState(value);
  if (value !== lastExternalValue) {
    setLastExternalValue(value);
    if (value !== lastCommittedRef.current) {
      lastCommittedRef.current = value;
      setLocalValue(value);
    }
  }

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      commit(localValue);
    },
    [commit, localValue]
  );

  // Clearing is a decision, not a keystroke: it commits straight away rather
  // than waiting out the idle timer.
  const handleClear = useCallback(() => {
    setLocalValue("");
    commit("");
  }, [commit]);

  const hasUncommittedText = localValue.trim() !== lastCommittedRef.current;

  return (
    <form onSubmit={handleSubmit} role="search" className={cn("relative w-56", className)}>
      {isBusy ? (
        <span
          role="status"
          aria-label={t.documents.table.searching}
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2"
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
        </span>
      ) : (
        <button
          type="submit"
          title={hasUncommittedText ? t.documents.table.pressEnterToSearch : undefined}
          aria-label={
            hasUncommittedText ? t.documents.table.pressEnterToSearch : t.documents.table.search
          }
          className={cn(
            "absolute left-2.5 top-1/2 -translate-y-1/2 transition-colors",
            hasUncommittedText ? "text-foreground" : "text-muted-foreground hover:text-foreground"
          )}
        >
          <Search className="h-3.5 w-3.5" />
        </button>
      )}
      <Input
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        placeholder={placeholder ?? t.documents.table.searchByName}
        aria-busy={isBusy}
        className={cn(CONTROL_FIELD_CLASS, "pl-8 pr-8")}
        {...pageInfoTarget("files.search")}
      />
      {localValue && (
        <button
          type="button"
          onClick={handleClear}
          aria-label={t.documents.table.clearSearch}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </form>
  );
}
